import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useFormatters } from '../../shared/format/use-formatters';
import {
  baseSportPriceCents,
  extraSportPriceCents,
  planSports,
  type PlanSport,
} from '../../shared/pricing/plan';
import { usePlanPrices } from '../../shared/pricing/use-plan-prices';
import { ButtonLink } from '../../shared/ui/button';
import { sectionLeadClass, sectionTitleClass } from './landing-classes';

const optionClass =
  'block cursor-pointer rounded-md border border-line px-3 py-2 text-center text-sm font-medium text-content has-checked:border-accent has-checked:bg-accent has-checked:text-on-accent has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-accent-ink';

export const Pricing = () => {
  const { t } = useTranslation();
  const { formatCurrency } = useFormatters();
  const { locale } = useParams();
  const prices = usePlanPrices();
  const [mainSport, setMainSport] = useState<PlanSport>('football');
  const [extras, setExtras] = useState<PlanSport[]>([]);
  const money = (cents: number) => formatCurrency(cents / 100);

  const selectMain = (sport: PlanSport) => {
    setMainSport(sport);
    setExtras((selected) => selected.filter((extra) => extra !== sport));
  };

  return (
    <section
      className="grid scroll-mt-20 gap-12 border-t border-line py-12 sm:py-20 lg:grid-cols-[1fr_1.1fr]"
      id="pricing"
      aria-labelledby="pricing-title"
    >
      <div>
        <h2 className={sectionTitleClass} id="pricing-title">
          {t('landing.pricing.title', prices)}
        </h2>
        <p className={sectionLeadClass}>{t('landing.pricing.description', prices)}</p>
        <p className="mt-10 text-5xl font-semibold tracking-tight text-content sm:text-6xl">
          {money(baseSportPriceCents)}
        </p>
        <p className="mt-1 text-sm text-muted">{t('landing.pricing.base')}</p>
        <p className="mt-6 text-2xl font-semibold tracking-tight text-accent-ink">
          + {money(extraSportPriceCents)}
        </p>
        <p className="mt-1 text-sm text-muted">{t('landing.pricing.extra')}</p>
      </div>
      <div className="rounded-xl border border-line bg-surface p-6 sm:p-8">
        <fieldset className="min-w-0">
          <legend className="text-sm font-semibold text-content">
            {t('landing.pricing.mainSport')}
          </legend>
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {planSports.map((sport) => (
              <label key={sport} className={optionClass}>
                <input
                  type="radio"
                  name="main-sport"
                  value={sport}
                  className="sr-only"
                  checked={mainSport === sport}
                  onChange={() => selectMain(sport)}
                />
                {t(`landing.sports.${sport}`)}
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset className="mt-8 min-w-0">
          <legend className="text-sm font-semibold text-content">
            {t('landing.pricing.extraSports')}
          </legend>
          <div className="mt-2 divide-y divide-line">
            {planSports
              .filter((sport) => sport !== mainSport)
              .map((sport) => (
                <label
                  key={sport}
                  className="flex cursor-pointer items-center gap-3 py-3 text-sm text-content"
                >
                  <input
                    type="checkbox"
                    className="size-4 rounded-sm border-line accent-accent"
                    checked={extras.includes(sport)}
                    onChange={(event) =>
                      setExtras(
                        event.target.checked
                          ? [...extras, sport]
                          : extras.filter((extra) => extra !== sport),
                      )
                    }
                  />
                  <span>{t(`landing.sports.${sport}`)}</span>
                  <span className="ml-auto text-sm text-muted">
                    + {money(extraSportPriceCents)}
                    {t('landing.pricing.perMonth')}
                  </span>
                </label>
              ))}
          </div>
        </fieldset>
        <div
          className="mt-6 flex flex-wrap items-baseline justify-between gap-2 border-t border-line pt-6"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <span className="text-sm text-content">{t('landing.pricing.total')}</span>
          <p>
            <strong className="text-3xl font-semibold tracking-tight text-content">
              {money(baseSportPriceCents + extras.length * extraSportPriceCents)}
            </strong>
            <span className="text-sm text-muted">{t('landing.pricing.perMonth')}</span>
          </p>
        </div>
        <p className="mt-3 text-sm text-muted">{t('landing.pricing.included', prices)}</p>
        <ButtonLink className="mt-6 w-full px-5 py-3 text-base sm:w-auto" to={`/${locale}/sign-up`}>
          {t('landing.hero.primaryAction', prices)}
        </ButtonLink>
      </div>
    </section>
  );
};
